import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { Activity, AlertTriangle, Clock, Users, Repeat, Armchair, TrendingUp, Download, RefreshCw } from 'lucide-react';
import {
  Bar, BarChart, CartesianGrid, ResponsiveContainer,
  Tooltip, XAxis, YAxis, Legend,
} from 'recharts';
import apiClient from '@/services/api';
import type { OperationsReport } from '@/types/api';
import { KPICard } from '@/components/reports/KPICard';
import type { ReportViewProps } from '@/components/reports/reportTypes';
import { Can } from '@/components/auth/Can';
import { PERM } from '@/constants/permissions';
import { buildOperationsCsv, downloadCsv, printOperationsReport } from '@/utils/reportExport';

const DOW = ['', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

/** Default Operations-tab report: table turns, occupancy, peak hours, covers, yield. */
export default function OperationsOverviewReport({ filters }: ReportViewProps) {
  const {
    selectedRestaurantId, startDate, endDate, formatCurrency, selectedRestaurant,
  } = filters;

  const { data: report, isLoading, isError, refetch } = useQuery<OperationsReport>({
    queryKey: ['operations-report', selectedRestaurantId, startDate, endDate],
    queryFn: () => apiClient.getOperationsReport(selectedRestaurantId!, startDate, endDate),
    enabled: !!selectedRestaurantId,
  });

  const exportMeta = () => ({
    restaurantName: selectedRestaurant?.name ?? '',
    startDate,
    endDate,
    formatCurrency,
  });

  const handleExportCsv = () => {
    if (!report) return;
    downloadCsv(`operations-report-${format(new Date(), 'yyyy-MM-dd')}.csv`, buildOperationsCsv(report, exportMeta()));
  };

  const handleExportPdf = () => {
    if (!report) return;
    printOperationsReport(report, exportMeta());
  };

  const heatGrid = (() => {
    const map = new Map<string, number>();
    (report?.occupancyHeatmap ?? []).forEach((c) => map.set(`${c.dayOfWeek}-${c.hour}`, c.intensity));
    return map;
  })();

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Operations Overview</h2>
          <p className="mt-1 text-sm text-gray-500">Table turns, occupancy, peak hours, covers, and yield.</p>
        </div>
        <Can name={PERM.REPORT_EXPORT}>
          <div className="flex gap-2">
            <button
              onClick={handleExportCsv}
              className="btn btn-outline flex items-center gap-2"
              disabled={!report}
            >
              <Download className="h-4 w-4" />
              Export CSV
            </button>
            <button
              onClick={handleExportPdf}
              className="btn btn-outline flex items-center gap-2"
              disabled={!report}
            >
              <Download className="h-4 w-4" />
              Export PDF
            </button>
          </div>
        </Can>
      </div>

      {!selectedRestaurantId ? (
        <div className="card"><div className="card-body text-center text-gray-500">Select a restaurant to view operations reports</div></div>
      ) : isLoading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      ) : isError ? (
        <div className="card"><div className="card-body p-8 text-center">
          <AlertTriangle className="h-8 w-8 mx-auto text-red-500 mb-3" />
          <p className="text-gray-900 font-medium mb-3">Failed to load operations report</p>
          <button onClick={() => refetch()} className="btn btn-outline h-10">
            <RefreshCw className="h-4 w-4 mr-2" />
            Retry
          </button>
        </div></div>
      ) : report ? (
        <div className="space-y-6">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            <KPICard label="Table Turns" value={Number(report.totalTurns).toLocaleString()} icon={Repeat} palette="blue" />
            <KPICard label="Covers" value={Number(report.totalCovers).toLocaleString()} icon={Users} palette="green" />
            <KPICard label="Avg Session" value={`${Number(report.avgSessionMinutes).toFixed(0)}m`} icon={Clock} palette="purple" />
            <KPICard label="Seat Occupancy" value={`${(Number(report.seatOccupancyRate) * 100).toFixed(0)}%`} icon={Armchair} palette="blue" />
            <KPICard label="RevPASH" value={formatCurrency(report.revPASH)} icon={TrendingUp} palette="green" />
            <KPICard label="Spend / Cover" value={formatCurrency(report.spendPerCover)} icon={Activity} palette="purple" />
          </div>

          <div className="card"><div className="card-body">
            <h2 className="text-lg font-semibold mb-4">Peak Hours (turns & covers by hour)</h2>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={report.peakHours}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="hour" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="turns" name="Turns" fill="#0088FE" />
                <Bar dataKey="covers" name="Covers" fill="#00C49F" />
              </BarChart>
            </ResponsiveContainer>
          </div></div>

          <div className="card"><div className="card-body">
            <h2 className="text-lg font-semibold mb-4">Occupancy Heatmap</h2>
            <div className="overflow-x-auto">
              <div className="inline-block">
                <div className="grid" style={{ gridTemplateColumns: `48px repeat(24, 20px)` }}>
                  <div></div>
                  {Array.from({ length: 24 }, (_, h) => (
                    <div key={`h-${h}`} className="text-[10px] text-gray-400 text-center">{h}</div>
                  ))}
                  {[1, 2, 3, 4, 5, 6, 7].map((d) => (
                    <div key={`row-${d}`} className="contents">
                      <div className="text-xs text-gray-500 pr-2 flex items-center">{DOW[d]}</div>
                      {Array.from({ length: 24 }, (_, h) => {
                        const v = heatGrid.get(`${d}-${h}`) ?? 0;
                        return (
                          <div key={`${d}-${h}`} title={`${DOW[d]} ${h}:00 — ${(v * 100).toFixed(0)}%`}
                            className="h-5 w-5 border border-white"
                            style={{ backgroundColor: `rgba(0,136,254,${v})` }} />
                        );
                      })}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div></div>

          <div className="card"><div className="card-body">
            <h2 className="text-lg font-semibold mb-4">Session Length Distribution</h2>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={report.sessionLengthHistogram}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="label" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="count" name="Sessions" fill="#8884D8" />
              </BarChart>
            </ResponsiveContainer>
          </div></div>

          <div className="card"><div className="card-body">
            <h2 className="text-lg font-semibold mb-4">Table Turns</h2>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50"><tr>
                  {['Table', 'Floor', 'Turns', 'Covers', 'Avg Session', 'Revenue'].map((h) => (
                    <th key={h} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{h}</th>
                  ))}
                </tr></thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {report.tableTurns.map((t) => (
                    <tr key={t.tableId}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{t.tableName || t.tableId}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{t.floorName ?? '—'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{t.turns}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{t.covers}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{Number(t.avgSessionMinutes).toFixed(0)}m</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatCurrency(t.revenue)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div></div>

          <div className="card"><div className="card-body">
            <h2 className="text-lg font-semibold mb-4">Server Performance</h2>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50"><tr>
                  {['Server', 'Turns', 'Covers', 'Avg Session', 'Revenue'].map((h) => (
                    <th key={h} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{h}</th>
                  ))}
                </tr></thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {report.serverPerformance.map((s, i) => (
                    <tr key={i}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{s.serverName}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{s.turns}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{s.covers}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{Number(s.avgSessionMinutes).toFixed(0)}m</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatCurrency(s.revenue)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div></div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <KPICard label="Bookings" value={Number(report.bookingOps.totalBookings).toLocaleString()} icon={Users} palette="blue" />
            <KPICard label="No-shows" value={Number(report.bookingOps.noShows).toLocaleString()} icon={Activity} palette="purple" />
            <KPICard label="No-show Rate" value={`${(Number(report.bookingOps.noShowRate) * 100).toFixed(0)}%`} icon={TrendingUp} palette="green" />
            <KPICard label="Avg Party" value={Number(report.bookingOps.avgPartySize).toFixed(1)} icon={Users} palette="blue" />
          </div>
        </div>
      ) : (
        <div className="card"><div className="card-body text-center text-gray-500">No data available</div></div>
      )}
    </div>
  );
}
